import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ENTITLEMENT_FEATURES, ENTITLEMENT_LIMITS, FEATURE_LABELS, LIMIT_LABELS, type EntitlementLimit, type PackageConfig, type PackageVersionView, type ProductSummaryView } from '@lp/contracts';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { errorText, fieldProblems, get, post, put } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { DeleteDraftButton } from '@/components/delete-draft';
import { SettingsEditor } from '@/components/settings';
import { Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader } from '@/components/ui';

/** the draft editor of a package: products, limits, features — then publish (a published version never changes) */
export function PackageEditPage() {
  const t = useT();
  const code = (useParams().code ?? '').toUpperCase();
  const version = Number(useParams().version);
  const nav = useNavigate();
  const qc = useQueryClient();
  const draft = useQuery({ queryKey: ['package', code, 'version', version], queryFn: () => get<PackageVersionView>(`/platform/packages/${code}/versions/${version}`) });
  const catalog = useQuery({ queryKey: ['products'], queryFn: () => get<ProductSummaryView[]>('/platform/products') });
  const [config, setConfig] = useState<PackageConfig | null>(null);
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirm, setConfirm] = useState('');
  useEffect(() => {
    if (draft.data && !config) {
      setConfig(draft.data.config);
      setNote(draft.data.note ?? '');
    }
  }, [draft.data, config]);
  const change = (c: PackageConfig) => {
    setConfig(c);
    setDirty(true);
  };
  const save = useMutation({
    mutationFn: () => put<PackageVersionView>(`/platform/packages/${code}/versions/${version}`, { config, ...(note.trim() ? { note } : {}) }),
    onSuccess: (v) => {
      qc.setQueryData(['package', code, 'version', version], v);
      setDirty(false);
    },
  });
  const publish = useMutation({
    mutationFn: () => post(`/platform/packages/${code}/versions/${version}/publish`, { confirmCode: confirm }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['package', code] });
      void qc.invalidateQueries({ queryKey: ['packages'] });
      nav(`/packages/${code}`);
    },
  });

  if (draft.isLoading || !config) return draft.error ? <ErrorBox text={errorText(draft.error)} /> : <Loading />;
  if (draft.data?.status !== 'draft') return <ErrorBox text={t('This version is published and never changes. Make a new version to change the package.')} />;

  const setLimit = (k: EntitlementLimit, v: number | null) => change({ ...config, limits: { ...config.limits, [k]: v } });
  const only = config.products === 'all' ? null : config.products;
  const toggleProduct = (p: string) => change({ ...config, products: only!.includes(p) ? only!.filter((x) => x !== p) : [...only!, p] });

  return (
    <>
      <Link to={`/packages/${code}`} className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {code}
      </Link>
      <PageHeader
        title={`${code} · ${t('Version')} ${version} · ${t('Draft')}`}
        subtitle={t('Nothing here is in force until it is published. Clients move to the new version from the package page.')}
        actions={
          <>
            <DeleteDraftButton
              path={`/platform/packages/${code}/versions/${version}`}
              label={`${code} v${version}`}
              onDeleted={(a) => {
                void qc.invalidateQueries({ queryKey: ['package', code] });
                void qc.invalidateQueries({ queryKey: ['packages'] });
                nav((a as { packageDeleted?: boolean } | undefined)?.packageDeleted ? '/packages' : `/packages/${code}`);
              }}
            />
            <Button variant="secondary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {t('Save')}
            </Button>
            <Button onClick={() => setPublishing(true)} disabled={dirty}>
              {t('Publish')}
            </Button>
          </>
        }
      />
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title={t('Products')}>
          <label className="mb-1.5 flex items-center gap-2 text-sm">
            <input type="radio" checked={config.products === 'all'} onChange={() => change({ ...config, products: 'all' })} />
            {t('Every product, including new ones')}
          </label>
          <label className="mb-2 flex items-center gap-2 text-sm">
            <input type="radio" checked={config.products !== 'all'} onChange={() => change({ ...config, products: [] })} />
            {t('Only these')}
          </label>
          {only && (
            <ul className="ml-5 space-y-1">
              {(catalog.data ?? []).map((p) => (
                <li key={p.code}>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={only.includes(p.code)} onChange={() => toggleProduct(p.code)} aria-label={p.code} />
                    <span className="font-mono text-xs">{p.code}</span> <span className="text-ink-600">{p.nameEn}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t('Limits')}>
          <div className="space-y-3">
            {ENTITLEMENT_LIMITS.map((k) => (
              <div key={k}>
                <Field label={t(LIMIT_LABELS[k])}>
                  <div className="flex items-center gap-3">
                    <Input type="number" min={0} value={config.limits[k] ?? ''} disabled={config.limits[k] == null} onChange={(e) => setLimit(k, e.target.value === '' ? 0 : Math.max(0, Math.floor(Number(e.target.value))))} className="w-28" aria-label={t(LIMIT_LABELS[k])} />
                    <label className="flex items-center gap-1.5 text-xs text-ink-600">
                      <input type="checkbox" checked={config.limits[k] == null} onChange={(e) => setLimit(k, e.target.checked ? null : 0)} />
                      {t('No limit')}
                    </label>
                  </div>
                </Field>
              </div>
            ))}
          </div>
        </Card>

        <Card title={t('Features')}>
          <ul className="space-y-1.5">
            {ENTITLEMENT_FEATURES.map((f) => (
              <li key={f}>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={config.features[f]} onChange={(e) => change({ ...config, features: { ...config.features, [f]: e.target.checked } })} />
                  {t(FEATURE_LABELS[f])}
                </label>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card title={t('Settings')} className="mt-4">
        <p className="mb-3 text-xs text-ink-500">{t('The package’s values: above the platform default, below a template and the company’s own values.')}</p>
        <SettingsEditor map={config.settings} products={(catalog.data ?? []).map((p) => p.code)} onChange={(settings) => change({ ...config, settings })} />
      </Card>

      <Card title={t('Note')} className="mt-4">
        <Input
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setDirty(true);
          }}
          maxLength={300}
          placeholder={t('What changed in this version')}
        />
      </Card>

      {publishing && (
        <Dialog
          title={`${t('Publish')} ${code} v${version}`}
          onClose={() => setPublishing(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPublishing(false)}>
                {t('Cancel')}
              </Button>
              <Button onClick={() => publish.mutate()} disabled={publish.isPending || confirm.trim().toUpperCase() !== code}>
                {t('Publish')}
              </Button>
            </>
          }
        >
          <p>{t('A published version never changes. Clients stay on their version until you move them.')}</p>
          <Field label={`${t('Type the package code to confirm')}: ${code}`}>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus />
          </Field>
          {publish.error && <ErrorBox text={[errorText(publish.error), ...fieldProblems(publish.error)].join(' — ')} />}
        </Dialog>
      )}
    </>
  );
}
